import { Button } from '@/components/ui/button';
import type {
  AttemptPhase,
  AttemptResult,
  BonusAttemptPrompt,
  PostAttemptPrompt,
} from '@/app/Teleop/teleopAttemptTypes';

type TeleopPromptOverlayProps = {
  attemptPhase: AttemptPhase;
  countdownValue: number | null;
  attemptResult: AttemptResult | null;
  readyMessage: string | null;
  awaitingResult: boolean;
  postAttemptPrompt: PostAttemptPrompt | null;
  bonusAttemptPrompt: BonusAttemptPrompt | null;
  onPlayAgain: () => void;
  onShare: () => void;
  onAcknowledgeBonusAttempt: () => void;
};

const BONUS_PROMPT_CLASS =
  'border-slate-300/40 bg-slate-900/70 text-slate-100 shadow-[0_20px_50px_rgba(15,23,42,0.45)]';

const TeleopPromptOverlay: React.FC<TeleopPromptOverlayProps> = ({
  attemptPhase,
  countdownValue,
  attemptResult,
  readyMessage,
  awaitingResult,
  postAttemptPrompt,
  bonusAttemptPrompt,
  onPlayAgain,
  onShare,
  onAcknowledgeBonusAttempt,
}) => {
  const waitingStatus =
    attemptPhase === 'waiting' &&
    !awaitingResult &&
    !attemptResult &&
    Boolean(readyMessage);
  const showCountdown =
    attemptPhase === 'countdown' &&
    countdownValue !== null;
  const countdownText =
    countdownValue === 0
      ? 'GO!'
      : countdownValue?.toString();
  const isSuccess = attemptResult?.success ?? false;
  const resultBadgeClass = isSuccess
    ? 'border-emerald-400/40 bg-emerald-500/20 text-emerald-100'
    : 'border-rose-400/40 bg-rose-500/20 text-rose-100';
  const glowClass = isSuccess
    ? 'from-emerald-400/35 via-emerald-300/15 to-transparent'
    : 'from-rose-400/35 via-rose-300/15 to-transparent';
  const ringClass = isSuccess
    ? 'border-emerald-300/60'
    : 'border-rose-300/60';
  const promptAccentClass = postAttemptPrompt?.success
    ? 'border-emerald-300/60 bg-emerald-500/35 text-emerald-50 shadow-[0_20px_50px_rgba(16,185,129,0.35)]'
    : 'border-rose-300/60 bg-rose-500/35 text-rose-50 shadow-[0_20px_50px_rgba(244,63,94,0.35)]';

  return (
    <>
      {waitingStatus ? (
        <div className="pointer-events-none absolute inset-x-0 top-[32vh] z-20 flex items-center justify-center px-6">
          <div className="flex w-full max-w-[360px] items-center gap-3 rounded-2xl border border-white/10 bg-black/45 px-5 py-4 text-white shadow-[0_20px_45px_rgba(0,0,0,0.35)] backdrop-blur-xl">
            <div className="relative flex h-10 w-10 items-center justify-center">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/30" />
              <span className="inline-flex h-3 w-3 rounded-full bg-primary shadow-[0_0_12px_rgba(69,240,223,0.8)]" />
            </div>
            <div className="flex flex-col gap-1">
              <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-white/70">
                You&apos;re up next
              </div>
              <div className="text-sm font-semibold text-white">
                {readyMessage}
              </div>
              <div className="text-[11px] text-white/70">
                We&apos;ll start as soon as the robot is ready.
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {showCountdown ? (
        <div className="pointer-events-none absolute inset-x-0 top-[28vh] z-30 flex items-center justify-center px-6">
          <div className="flex w-full max-w-[320px] flex-col items-center gap-2 rounded-3xl border border-white/10 bg-black/55 px-6 py-5 text-white shadow-[0_24px_55px_rgba(0,0,0,0.45)] backdrop-blur-2xl">
            <div className="text-[11px] font-semibold uppercase tracking-[0.3em] text-white/70">
              Control starts in
            </div>
            <div className="text-[64px] font-semibold leading-none text-white">
              {countdownText}
            </div>
            <div className="text-[12px] text-white/70">
              Get ready to grab.
            </div>
          </div>
        </div>
      ) : null}
      {attemptResult ? (
        <div
          key={attemptResult.seq}
          className="absolute left-1/2 top-[calc(env(safe-area-inset-top,0px)+70px)] z-10 -translate-x-1/2 max-[600px]:top-[calc(env(safe-area-inset-top,0px)+62px)]"
        >
          <div
            className={`relative flex items-center justify-center ${isSuccess ? '' : 'attempt-shake'}`}
          >
            <div
              className={`attempt-glow pointer-events-none absolute h-32 w-80 -translate-y-2 rounded-full bg-gradient-to-b ${glowClass} blur-3xl`}
            />
            {isSuccess ? (
              <div
                className={`attempt-ring pointer-events-none absolute h-20 w-56 rounded-full border ${ringClass}`}
              />
            ) : null}
            <div
              className={`attempt-animate ${isSuccess ? '' : 'fail'} rounded-full border px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] ${resultBadgeClass}`}
            >
              {attemptResult.success ? 'SUCCESS' : 'FAIL'}
              {attemptResult.reason ? `: ${attemptResult.reason}` : ''}
            </div>
          </div>
        </div>
      ) : null}
      {postAttemptPrompt ? (
        <div className="pointer-events-none absolute inset-x-0 bottom-[35vh] z-20 flex items-center justify-center px-4 max-[600px]:bottom-[30vh]">
          <div
            className={`pointer-events-auto flex w-full max-w-[360px] flex-col gap-3 rounded-2xl border px-4 py-3 text-xs backdrop-blur-xl ${promptAccentClass}`}
          >
            <div className="flex items-center justify-between gap-3">
              <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/70">
                {postAttemptPrompt.success ? 'Nice grab!' : 'Nice try!'}
              </span>
              {postAttemptPrompt.reason ? (
                <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-white/80">
                  {postAttemptPrompt.reason}
                </span>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="h-9 flex-1 rounded-full border border-white/15 bg-white/15 text-[11px] font-semibold uppercase tracking-[0.2em] text-white hover:bg-white/25"
                onClick={onPlayAgain}
              >
                Play again
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-9 flex-1 rounded-full border border-white/10 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/80 hover:bg-white/10"
                onClick={onShare}
              >
                Share
              </Button>
            </div>
          </div>
        </div>
      ) : null}
      {bonusAttemptPrompt ? (
        <div className="pointer-events-none absolute inset-x-0 bottom-[35vh] z-20 flex items-center justify-center px-4 max-[600px]:bottom-[30vh]">
          <div
            className={`pointer-events-auto flex w-full max-w-[360px] flex-col gap-3 rounded-2xl border px-4 py-3 text-xs backdrop-blur-xl ${BONUS_PROMPT_CLASS}`}
          >
            <div className="flex items-center justify-between gap-3">
              <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/75">
                Bonus Attempt Unlocked
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-white/80">
                Attempt {bonusAttemptPrompt.nextAttempt}/
                {bonusAttemptPrompt.totalAttempts}
              </span>
            </div>
            <div className="text-[11px] text-white/75">
              Get ready for your next attempt.
            </div>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="h-9 rounded-full border border-white/15 bg-white/15 text-[11px] font-semibold uppercase tracking-[0.2em] text-white hover:bg-white/25"
              onClick={onAcknowledgeBonusAttempt}
            >
              Ok
            </Button>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default TeleopPromptOverlay;
